import store from '../store'
import debounce from 'debounce'
import { captureLog } from '@/helpers/log'

const SOUND_DEBOUNCE = 1000

const sounds = {
	USER: '/audio/user_message.wav',
	SYSTEM: '/audio/system_message.wav',
	SILENT: '/audio/silence.mp3',
	MEETING: '/audio/videosession.wav',
	TASK: '/audio/task_reminder.wav',
}

const hasSoundsEnabled = () =>
	store.auth?.user?.webnotificationsoundsenabled ?? false

const playSound = async (pathToAudio, options = {}) => {
	if (!hasSoundsEnabled() || window.todesktop) return false
	try {
		const audio = new Audio(pathToAudio)
		Object.assign(audio, options)
		await audio.play()
	} catch (e) {
		console.debug('cannot play notification audio', e)
	}
}

const playSoundDebounced = debounce(playSound, SOUND_DEBOUNCE)

function genericNotification({ title, alert }, options = {}) {
	if (!hasSoundsEnabled()) {
		// ToDesktop has a bug where sound is still audible
		// even when user has disabled sounds at the OS level. So this
		// is a workaround to remove custom sound if user has
		// webnotificationsoundsenabled set to false
		if (window.todesktop) {
			options.sound = sounds.SILENT
		} else {
			delete options.sound
		}
	}
	captureLog('PushNotification', title, alert, options)
	return new Notification(title, {
		body: alert,
		...options,
	})
}

function announcement(pn) {
	return new Notification('Announcement', {
		body: pn.alert,
		sound: sounds.USER,
	})
}

function message(pn) {
	playSoundDebounced(sounds.USER)
	return genericNotification(pn, {
		icon: store.userProfile(pn.message.user),
		tag: pn.message.id,
		sound: sounds.USER,
	})
}

function poll(pn) {
	playSoundDebounced(sounds.USER)
	return genericNotification(pn, {
		icon: store.userProfile(pn.message.poll.user),
		tag: pn.message.id,
		sound: sounds.USER,
	})
}

function systemmessage(pn) {
	playSoundDebounced(sounds.SYSTEM, { volume: 0.5 })
	return genericNotification(pn, { sound: sounds.SYSTEM })
}

function taskreminder(pn) {
	playSoundDebounced(sounds.TASK)
	return genericNotification(pn, {
		body: pn.task.title,
		requireInteraction: true,
		sound: sounds.TASK,
	})
}

function videosession(pn) {
	playSoundDebounced(sounds.MEETING)
	return genericNotification(pn, {
		body: pn.alert,
		sound: sounds.MEETING,
	})
}

export function pushNotification(pn) {
	let notification = null
	switch (pn.type) {
		case 'announcement':
			notification = announcement(pn)
			break
		case 'markupdate':
			// not implemented
			return
		case 'message':
			notification = message(pn)
			break
		case 'poll':
			notification = poll(pn)
			break
		case 'systemmessage':
			notification = systemmessage(pn)
			break
		case 'taskreminder':
			notification = taskreminder(pn)
			break
		case 'videosession':
			notification = videosession(pn)
			break
		default:
			notification = genericNotification(pn, { sound: sounds.USER })
			break
	}
	storeNotification(pn.bubble_id, notification)
	return notification
}

const notificationsByBubbleId = {}

export const storeNotification = (bubble_id, notification) => {
	if (!bubble_id || !notification) return
	const { [bubble_id]: list = [] } = notificationsByBubbleId
	notificationsByBubbleId[bubble_id] = list.concat(notification)
}

export const closeNotificationsForBubble = (bubble_id) => {
	const list = notificationsByBubbleId[bubble_id] || []
	list.filter((n) => n).forEach((n) => n.close())
	notificationsByBubbleId[bubble_id] = []
}
